/**
 * SharePoint Environment Configuration
 * Loads configuration from SharePointConfigContext (stored in localStorage) or falls back to environment variables.
 * 
 * Note: In browser context, we can't read .env files directly.
 * The SharePointConfigContext loads values from the database and stores them in localStorage.
 * 
 * IMPORTANT: These functions read from localStorage dynamically, not at module load time,
 * because the config is loaded asynchronously when the user clicks on an engagement.
 */

/** Parsed `sharepoint_config` from localStorage (no CLIENT_SECRET requirement). */
export function getRawSharePointConfigFromLocalStorage(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem('sharepoint_config');
    if (!stored) return null;
    const config = JSON.parse(stored);
    return config && typeof config === 'object' ? config : null;
  } catch (error) {
    console.error('Error loading SharePoint config from localStorage:', error);
    return null;
  }
}

/** `DOC_LIBRARY (FY_YEAR)` for UI labels; uses config then current engagement. */
export function getEngagementEntityLabel(options?: {
  docLibrary?: string;
  fyYear?: string;
  clientName?: string;
  engagementName?: string;
}): string {
  const raw = getRawSharePointConfigFromLocalStorage();
  const lib = (
    options?.docLibrary?.trim() ||
    raw?.DOC_LIBRARY?.trim() ||
    getDOC_LIBRARY().trim() ||
    options?.clientName?.trim() ||
    ''
  );
  const fy = (
    options?.fyYear?.trim() ||
    raw?.FY_YEAR?.trim() ||
    getFY_YEAR().trim() ||
    options?.engagementName?.trim() ||
    ''
  );
  return `${lib || '—'} (${fy || '—'})`;
}

export const SHAREPOINT_CONFIG_UPDATED_EVENT = 'sharepoint-config-updated';

// Function to get config from storage (localStorage in browser, file in Node.js)
function getConfigFromStorage() {
  // Browser context: read from localStorage
  if (typeof window !== 'undefined') {
    const config = getRawSharePointConfigFromLocalStorage();
    // Validate that required fields are present (needed for API / Graph calls)
    if (config && config.CLIENT_SECRET && config.CLIENT_SECRET.trim() !== '') {
      return config;
    }
  }
  
  // Node.js context (Electron main process): read from file
  if (typeof process !== 'undefined' && process.versions && process.versions.electron) {
    try {
      const fs = require('fs');
      const path = require('path');
      const { app } = require('electron');
      
      if (app && app.isReady && app.isReady()) {
        const userDataPath = app.getPath('userData');
        const configPath = path.join(userDataPath, 'sharepoint_config.json');
        
        if (fs.existsSync(configPath)) {
          const fileContent = fs.readFileSync(configPath, 'utf8');
          const config = JSON.parse(fileContent);
          // Validate that required fields are present
          if (config && config.CLIENT_SECRET && config.CLIENT_SECRET.trim() !== '') {
            return config;
          }
        }
      }
    } catch (error) {
      // app might not be ready yet, or file doesn't exist - that's okay
      // console.error('Error loading SharePoint config from file:', error);
    }
  }
  
  return null;
}

// Export functions that read from localStorage dynamically
// This ensures we always get the latest config, even if it was loaded after module initialization
export function getTENANT_ID(): string {
  const config = getConfigFromStorage();
  return config?.TENANT_ID || import.meta.env.VITE_TENANT_ID || '';
}

export function getCLIENT_ID(): string {
  const config = getConfigFromStorage();
  return config?.CLIENT_ID || import.meta.env.VITE_CLIENT_ID || '';
}

export function getCLIENT_SECRET(): string {
  const config = getConfigFromStorage();
  const secret = config?.CLIENT_SECRET || import.meta.env.VITE_CLIENT_SECRET || '';
  if (!secret || secret.trim() === '') {
    console.error('CLIENT_SECRET is empty! Config:', config);
  }
  return secret;
}

export function getSITE_HOSTNAME(): string {
  const config = getConfigFromStorage();
  return config?.SITE_HOSTNAME || import.meta.env.VITE_SITE_HOSTNAME || '';
}

export function getSITE_PATH(): string {
  const config = getConfigFromStorage();
  return config?.SITE_PATH || import.meta.env.VITE_SITE_PATH || '';
}

export function getDOC_LIBRARY(): string {
  const raw = getRawSharePointConfigFromLocalStorage();
  if (raw?.DOC_LIBRARY?.trim()) return raw.DOC_LIBRARY.trim();
  const config = getConfigFromStorage();
  return config?.DOC_LIBRARY || import.meta.env.VITE_DOC_LIBRARY || '';
}

export function getFY_YEAR(): string {
  const raw = getRawSharePointConfigFromLocalStorage();
  if (raw?.FY_YEAR?.trim()) return raw.FY_YEAR.trim();
  const config = getConfigFromStorage();
  return config?.FY_YEAR || import.meta.env.VITE_FY_YEAR || '';
}

export function getUSERNAME(): string {
  const raw = getRawSharePointConfigFromLocalStorage();
  if (raw?.USERNAME?.trim()) return raw.USERNAME.trim();
  const config = getConfigFromStorage();
  return config?.USERNAME || import.meta.env.VITE_USERNAME || '';
}

// Create a class that acts like a string but reads from localStorage dynamically
class ConfigValue {
  constructor(private getter: () => string, private name: string) {}
  
  valueOf(): string {
    const value = this.getter();
    if (!value && this.name === 'CLIENT_SECRET') {
      console.error(`[sharepoint-env] ${this.name} is empty in valueOf()! Check localStorage for 'sharepoint_config'`);
    }
    return value;
  }
  
  toString(): string {
    const value = this.getter();
    if (!value && this.name === 'CLIENT_SECRET') {
      console.error(`[sharepoint-env] ${this.name} is empty in toString()! Check localStorage for 'sharepoint_config'`);
    }
    return value;
  }
  
  [Symbol.toPrimitive](hint: 'string' | 'number' | 'default'): string {
    const value = this.getter();
    if (!value && this.name === 'CLIENT_SECRET') {
      console.error(`[sharepoint-env] ${this.name} is empty in toPrimitive(${hint})! Check localStorage for 'sharepoint_config'`);
    }
    return value;
  }
  
  // Make it work when used as a property value (MSAL library might check typeof)
  // Override all possible property access
  get [Symbol.toStringTag]() {
    return 'String';
  }
}

// Export configuration values as ConfigValue instances
// These read from localStorage dynamically each time they're accessed
// They work as strings in template literals, concatenation, and direct usage
export const TENANT_ID = new ConfigValue(getTENANT_ID, 'TENANT_ID') as any as string;
export const CLIENT_ID = new ConfigValue(getCLIENT_ID, 'CLIENT_ID') as any as string;
export const CLIENT_SECRET = new ConfigValue(getCLIENT_SECRET, 'CLIENT_SECRET') as any as string;
export const SITE_HOSTNAME = new ConfigValue(getSITE_HOSTNAME, 'SITE_HOSTNAME') as any as string;
export const SITE_PATH = new ConfigValue(getSITE_PATH, 'SITE_PATH') as any as string;
export const DOC_LIBRARY = new ConfigValue(getDOC_LIBRARY, 'DOC_LIBRARY') as any as string;
export const FY_YEAR = new ConfigValue(getFY_YEAR, 'FY_YEAR') as any as string;
export const USERNAME = new ConfigValue(getUSERNAME, 'USERNAME') as any as string;

